import { useEffect, useRef, useState } from "react";
import { BleClient, ScanResult } from "@capacitor-community/bluetooth-le";

export interface ScannedDevice {
  deviceId: string;
  name: string;
  rssi: number; // dBm — closer to 0 = stronger
  serviceUuids: string[];
  lastSeenAt: number;
}

export interface UseBleScanOptions {
  /** Filter advertisements to these service UUIDs. Empty/undefined = unfiltered. */
  services?: string[];
  /** Auto-stop scan after this many ms. Default 15s. */
  durationMs?: number;
}

interface UseBleScanReturn {
  devices: ScannedDevice[];
  scanning: boolean;
  error: string | null;
  /** Stop and start a new scan. */
  rescan: (opts?: UseBleScanOptions) => Promise<void>;
  /** Stop scanning. */
  stop: () => Promise<void>;
}

/**
 * Programmatic BLE scan with live device list and RSSI tracking.
 * Replaces `BleClient.requestDevice` so we can render our own picker.
 */
export function useBleScan(): UseBleScanReturn {
  const [devices, setDevices] = useState<ScannedDevice[]>([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(false);

  const stop = async () => {
    activeRef.current = false;
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    try {
      await BleClient.stopLEScan();
    } catch {
      // ignore — likely not scanning
    }
    setScanning(false);
  };

  const rescan = async (opts: UseBleScanOptions = {}) => {
    const { services, durationMs = 15_000 } = opts;
    setError(null);
    setDevices([]);
    await stop();

    try {
      await BleClient.initialize({ androidNeverForLocation: true });
      const isEnabled = await BleClient.isEnabled();
      if (!isEnabled) {
        try {
          await BleClient.requestEnable();
        } catch {
          throw new Error("Bluetooth is off. Please turn it on.");
        }
      }

      activeRef.current = true;
      setScanning(true);

      await BleClient.requestLEScan(
        { services: services && services.length ? services : undefined, allowDuplicates: true },
        (result: ScanResult) => {
          if (!activeRef.current) return;
          const id = result.device.deviceId;
          const name = result.device.name || result.localName || "Unknown";
          const rssi = result.rssi ?? -100;
          const serviceUuids = (result.uuids || []).map((u) => u.toLowerCase());

          setDevices((prev) => {
            const next = [...prev];
            const idx = next.findIndex((d) => d.deviceId === id);
            const entry: ScannedDevice = {
              deviceId: id,
              name,
              rssi,
              serviceUuids,
              lastSeenAt: Date.now(),
            };
            if (idx >= 0) {
              // Merge — preserve existing service UUIDs (advertisement may not always include them).
              next[idx] = {
                ...entry,
                serviceUuids: Array.from(new Set([...next[idx].serviceUuids, ...serviceUuids])),
                name: next[idx].name === "Unknown" ? entry.name : next[idx].name,
              };
            } else {
              next.push(entry);
            }
            // Sort by signal strength (strongest first).
            next.sort((a, b) => b.rssi - a.rssi);
            return next;
          });
        }
      );

      stopTimerRef.current = setTimeout(() => {
        if (activeRef.current) {
          stop();
        }
      }, durationMs);
    } catch (err: any) {
      setError(err?.message || "Failed to start Bluetooth scan.");
      setScanning(false);
      activeRef.current = false;
    }
  };

  // Stop on unmount.
  useEffect(() => {
    return () => {
      activeRef.current = false;
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      BleClient.stopLEScan().catch(() => {});
    };
  }, []);

  return { devices, scanning, error, rescan, stop };
}

export function rssiToBars(rssi: number): 1 | 2 | 3 | 4 {
  if (rssi >= -55) return 4;
  if (rssi >= -70) return 3;
  if (rssi >= -85) return 2;
  return 1;
}
